import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import CONSTANTS from "../utils/constants";
import { useTranslation } from "react-i18next";

export default function AdminBanner() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = Cookies.get("user")
          ? JSON.parse(Cookies.get("user"))
          : null;
        if (!user?.id) {
          navigate("/admin");
          return;
        }

        const permissionsResponse = await axios.get(
          `${CONSTANTS.baseRoute}/permissions`,
          {
            params: { user_id: user.id },
          }
        );
        const permissions = permissionsResponse.data.permissions;
        setUserPermissions(permissions);

        if (!permissions.includes(15)) {
          setError(t("adminBanner.noPermission"));
          navigate("/admin/home");
          return;
        }

        const bannersResponse = await axios.get(
          `${CONSTANTS.baseRoute}/banners/admin`
        );
        setBanners(bannersResponse.data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.response?.data?.error || t("adminBanner.loadError"));
        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate("/admin");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, t]);

  const handleDelete = async (id, name) => {
    if (!userPermissions.includes(18)) {
      setError(t("adminBanner.noDeletePermission"));
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (!window.confirm(t("adminBanner.confirmDelete", { name }))) return;

    try {
      await axios.delete(`${CONSTANTS.baseRoute}/banners/${id}`);
      setBanners((prev) => prev.filter((banner) => banner.id !== id));
      setError(t("adminBanner.deleteSuccess"));
      setTimeout(() => setError(""), 3000);
    } catch (err) {
      console.error("Error deleting banner:", err);
      setError(err.response?.data?.error || t("adminBanner.deleteError"));
      setTimeout(() => setError(""), 3000);
    }
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="7" className="text-center py-4">
            {t("common.loading")}
          </td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td
            colSpan="7"
            className={`text-center text-${
              error.includes(t("adminBanner.deleteSuccess"))
                ? "success"
                : "danger"
            } py-4`}
          >
            {error}
          </td>
        </tr>
      );
    }

    if (banners.length === 0) {
      return (
        <tr>
          <td colSpan="7" className="text-center py-4">
            {t("adminBanner.noBanners")}
          </td>
        </tr>
      );
    }

    return banners.map((banner) => (
      <tr
        key={banner.id}
        style={{ cursor: userPermissions.includes(17) ? "pointer" : "default" }}
        onClick={(e) => {
          if (["BUTTON", "I"].includes(e.target.tagName)) return;
          if (userPermissions.includes(17)) {
            navigate(`/admin/home/banner/edit/${banner.id}`);
          }
        }}
      >
        <td>
          {banner.image ? (
            <img
              src={`${CONSTANTS.baseRoute}${banner.image}`}
              alt={banner.name || "Banner"}
              style={{
                width: "100px",
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
          ) : (
            <div
              style={{
                width: "40px",
                height: "32px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="bi bi-card-image text-muted"></i>
            </div>
          )}
        </td>
        <td className="text-start align-middle">{banner.name || "-"}</td>
        {/* <td className="align-middle">{banner.category || "-"}</td> */}
        <td className="align-middle">{banner.order_number || 0}</td>
        <td className="align-middle">
          {banner.is_active ? (
            <i className="bi bi-check-circle-fill text-success fs-4"></i>
          ) : (
            <i className="bi bi-x-circle-fill text-danger fs-4"></i>
          )}
        </td>
        <td className="align-middle text-end">
          {userPermissions.includes(17) && (
            <button
              className="btn btn-sm btn-outline-primary me-2"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/home/banner/edit/${banner.id}`);
              }}
              title={t("common.edit")}
            >
              <i className="bi bi-pencil-fill"></i>
            </button>
          )}
          {userPermissions.includes(18) && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(banner.id, banner.name);
              }}
              title={t("common.delete")}
            >
              <i className="bi bi-trash-fill"></i>
            </button>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <div className="admin-main p-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2 align-items-center">
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <button className="btn btn-light" onClick={() => navigate(+1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <h5 className="fw-bold mb-0">{t("adminBanner.title")}</h5>
        </div>

        {userPermissions.includes(16) && (
          <button
            onClick={() => navigate("/admin/home/banner/add")}
            className="btn btn-outline-primary d-flex align-items-center gap-2"
          >
            <i className="bi bi-plus-circle"></i>
            {t("adminBanner.addNew")}
          </button>
        )}
      </div>

      <div className="card bg-light p-3 shadow-sm">
        <div className="table-responsive">
          <table
            className="table table-hover table-light mb-0"
            style={{ fontSize: "14px" }}
          >
            <thead style={{ backgroundColor: "#F47D39", color: "#fff" }}>
              <tr >
                <th style={{ width: "12%" }}>{t("adminBanner.image")}</th>
                <th className="text-start">{t("adminBanner.name")}</th>
                {/* <th style={{ width: "18%" }}>{t("adminBanner.section")}</th> */}
                <th style={{ width: "10%" }}>{t("adminBanner.order")}</th>
                <th style={{ width: "10%" }}>{t("adminBanner.active")}</th>
                <th style={{ width: "15%" }}></th>
              </tr>
            </thead>
            <tbody>{renderTableContent()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
